import React, { useState } from "react";
import { useNavigate } from "react-router-dom";
import { Plus, Sparkles, ArrowUpRight } from "lucide-react";
import { useApp } from "../context/AppContext";

// Per-product ambient backdrop + spotlight tuned to a soft, luxurious rose/copper palette
const THEMES = {
  "copper-peptide-serum-30ml": {
    bg: "from-[#F3E0D6] via-[#EFD3C6] to-[#E7C3B4]",
    glow: "rgba(212,150,128,0.55)",
  },
  "color-lip-tint": {
    bg: "from-[#F7E4EA] via-[#F2D3DE] to-[#EBC0D0]",
    glow: "rgba(228,150,178,0.5)",
  },
  "lip-gloss": {
    bg: "from-[#F5E3DC] via-[#F0D6CE] to-[#E9C7BD]",
    glow: "rgba(220,158,140,0.5)",
  },
  "lipliner": {
    bg: "from-[#F1E0D8] via-[#EBCFC3] to-[#E3BBA9]",
    glow: "rgba(200,120,92,0.5)",
  },
};

const ProductCard = ({ product, delay = 0 }) => {
  const { t, lang, addToCart, openWaitlist } = useApp();
  const navigate = useNavigate();
  const hasShades = Array.isArray(product.shades) && product.shades.length > 0;
  const [shade, setShade] = useState(hasShades ? product.shades[0] : null);
  const theme = THEMES[product.id] || THEMES["lip-gloss"];

  const goToDetail = () => navigate(`/product/${product.id}`);
  const onAction = (e) => {
    e.stopPropagation();
    if (product.preorder) openWaitlist(product.id);
    else addToCart(product, shade, 1);
  };

  return (
    <div
      className="group relative flex flex-col bg-white rounded-[1.75rem] border border-[#EEE0D7] overflow-hidden hover:shadow-[0_45px_90px_-45px_rgba(150,95,72,0.5)] hover:-translate-y-2 transition-all duration-[600ms] rise-in"
      style={{ animationDelay: `${delay}ms` }}
    >
      {/* ---- Visual stage ---- */}
      <div className={`relative aspect-[4/5] overflow-hidden bg-gradient-to-br ${theme.bg}`}>
        {/* soft spotlight */}
        <div
          className="absolute inset-0 opacity-90"
          style={{ background: `radial-gradient(60% 55% at 50% 42%, ${theme.glow}, transparent 70%)` }}
        />
        {/* ghosted serif number */}
        <span className="ghost-number absolute -top-2 left-4 text-[130px] select-none pointer-events-none">
          {product.index}
        </span>
        {/* decorative orbit ring */}
        <svg className="orbit absolute -right-10 -bottom-10 h-52 w-52 opacity-30 pointer-events-none" viewBox="0 0 200 200" fill="none">
          <circle cx="100" cy="100" r="78" stroke="#B4715C" strokeWidth="0.75" strokeDasharray="3 7" />
          <circle cx="100" cy="22" r="2.5" fill="#B4715C" />
        </svg>
        {/* twinkle accents */}
        <Sparkles className="twinkle absolute top-8 right-8 h-4 w-4 text-[#C79A7E]" />
        <span className="twinkle absolute bottom-14 left-10 h-1.5 w-1.5 rounded-full bg-[#B4715C]" style={{ animationDelay: "1s" }} />

        {/* tag + preorder */}
        <div className="absolute top-4 left-4 z-20 flex items-center gap-2">
          <span className="text-[10px] tracking-label uppercase px-2.5 py-1 rounded-full bg-white/70 backdrop-blur-sm text-[#8A6A5C] border border-white/60">
            {product.tag}
          </span>
        </div>
        {product.preorder && (
          <span className="absolute top-4 right-4 z-20 text-[10px] tracking-label uppercase px-3 py-1 rounded-full bg-[#3A2C28] text-[#F4E6DE] shadow-lg">
            {t.preorder}
          </span>
        )}

        {/* product image, floating */}
        <img
          src={product.image}
          alt={product.name[lang]}
          onClick={goToDetail}
          className="product-float relative z-10 h-full w-full object-contain p-8 cursor-pointer"
        />

        {/* glossy shine sweep on hover */}
        <span className="shine-sweep z-20" />
        {/* reflection fade at base */}
        <div className="absolute bottom-0 inset-x-0 h-16 bg-gradient-to-t from-white/40 to-transparent pointer-events-none z-10" />
      </div>

      {/* thin divider accent */}
      <div className="h-px w-full bg-gradient-to-r from-transparent via-[#E3C7B9] to-transparent" />

      {/* ---- Details ---- */}
      <div className="flex flex-col flex-1 p-7">
        <div className="flex items-center gap-2">
          <span className="font-serif text-[#C79A7E] text-lg leading-none">{product.index}</span>
          <span className="text-[11px] tracking-label uppercase text-[#A08678]">{product.category[lang]}</span>
        </div>
        <h3 onClick={goToDetail} className="font-serif text-[26px] text-[#3A2C28] mt-2.5 leading-tight cursor-pointer hover:text-[#B4715C] transition-colors">{product.name[lang]}</h3>
        <p className="text-[11px] tracking-label uppercase text-[#B4715C] mt-1">{product.subtitle[lang]}</p>
        <p className="text-sm text-[#6E5A52] mt-3.5 leading-relaxed flex-1">{product.short[lang]}</p>

        {hasShades && (
          <div className="mt-6">
            <div className="flex items-center justify-between mb-3">
              <p className="text-[11px] tracking-label uppercase text-[#8A756C]">{t.chooseShade}</p>
              <span className="text-[11px] text-[#B4715C]">{shade?.code} · {shade?.name}</span>
            </div>
            <div className="flex items-center gap-3.5">
              {product.shades.map((s) => (
                <button
                  key={s.code}
                  onClick={() => setShade(s)}
                  aria-label={`Shade ${s.code}`}
                  className={`relative h-9 w-9 rounded-full transition-all duration-300 ${
                    shade?.code === s.code
                      ? "ring-2 ring-offset-2 ring-[#B4715C] scale-110"
                      : "ring-1 ring-[#E8D6CB] hover:scale-105"
                  }`}
                  style={{ backgroundColor: s.hex, boxShadow: "inset 0 -3px 6px rgba(0,0,0,0.15), inset 0 3px 5px rgba(255,255,255,0.5)" }}
                />
              ))}
            </div>
          </div>
        )}

        <div className={`flex items-end justify-between ${hasShades ? "mt-7" : "mt-6"}`}>
          <div>
            <span className="block text-[10px] tracking-label uppercase text-[#A08678] mb-0.5">{product.volume}</span>
            <span className="font-serif text-[30px] text-[#3A2C28] leading-none">€{product.price.toFixed(2)}</span>
          </div>
          <button
            onClick={onAction}
            className="group/btn flex items-center gap-2 h-12 px-6 rounded-full bg-[#3A2C28] text-white text-[11px] tracking-label uppercase hover:bg-[#B4715C]"
          >
            {product.preorder ? t.preorderCta : t.addToCart}
            {product.preorder ? (
              <ArrowUpRight className="h-4 w-4 transition-transform group-hover/btn:translate-x-0.5 group-hover/btn:-translate-y-0.5" />
            ) : (
              <Plus className="h-4 w-4 transition-transform group-hover/btn:rotate-90" />
            )}
          </button>
        </div>
      </div>
    </div>
  );
};

export default ProductCard;
